import {
    DoppeDtoAction,
    doppeDtoActionExtendWithDefaultProps,
    doppeDtoActionGetDefaultLockSettingsProps,
    doppeDtoActionGetDefaultSearchSettingsProps
} from '../types/doppe-dto-action';
import type React from 'react';
import type {DoppeActionComponentActionButtonProps, IDoppeViewerActionContext} from '../../doppe-sdk';
import {
    arrayRemoveNullishValues,
    enumReduce,
    Evaluable,
    HideableValueHiddenState,
    IsAny,
    iterableMapToArray,
    objectGetEntries,
    objectMapValues,
    ObservableValue,
    OmitStrict,
    OverridableValueOriginalState,
    PartiallyRequired,
    Values, hideableValueCreateHidden } from '@wix/devzai-utils-common';
import {v4 as uuidv4} from 'uuid';
import type {WixImageResource} from '@wix/devzai-common-wix';
import type {
    DoppeAutomationTriggers,
    DoppeBusinessEntity,
    DoppeBusinessEntityReference,
    DoppeDtoCouponCodeData,
    DoppeExternalDataSource,
    DoppeListActionSettings
} from '../types';
import type {DoppeLinkedWixSiteSupportedTpas, DoppeSupportedWixTpas} from '../types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../types/doppe-dto-user';
import {DoppeListDataItemsSource, type DoppeListDataItemsSourceInfo} from '../doppe-list-data-items-source/doppe-list-data-items-source';
import type {DoppeActionLayout} from '../types/doppe-action-layout';
import type {DoppeActionWidgetPopupMode} from '../types/doppe-action-widget-popup-mode';
import type {DoppePremiumFeaturesStatus} from '../doppe-premium';
import type {DoppeActionContentSectionsSettings} from "../types/doppe-action-content-sections-settings";
import type {
    BymoPagePropsContentSettingsMetadata,
    DoppeActionTypeContentSpecialSettingsMetadata
} from "../bymo-page-props-metadata/bymo-page-props-metadata";
import type {BymoPageMedia, BymoPageProps} from "../bymo-pages";
import type {BymoPageContextProps} from "../../doppe-sdk/bymo-page/bymo-page-context-props";
import type {DoppeActionTypeSpecificContentSectionTypes} from "../types/doppe-action-content-section";
import type {DoppeActionCtaButtonsSettings} from "../types/doppe-action-cta-buttons-settings";
import type {DoppeActionTypeSpecificCTATypes} from "../types/doppe-action-cta-button";
import type {DoppeViewerLang} from "../doppe-viewer/doppe-viewer-lang";
import {DoppeActionTypeRenderingInPageSupport} from "./doppe-action-type-rendering-in-page-support";
import type {DoppeViewerPagesRoutesManager} from "../viewer-pages-routes-manager/doppe-viewer-pages-routes-manager";
import ListSearchSettings = DoppeDtoAction.ListSearchSettings;


export type DoppeActionTypeViewerMetadata<PROPS extends {}, VIEWER_COMP = any> = {
    /**
     * An optional function that provides an in-page view for an instance of this action type, that's an
     * alternative for the standard action button.
     */
    renderWidget?: (renderProps: DoppeActionType.WidgetProps<PROPS>, viewerComponents: VIEWER_COMP) => React.ReactElement;

    /**
     * An action type can provide this function that returns the properties that should be used to render
     * the action component for it, when it's rendered as an action button.
     */
    getActionButtonProps?: (actionContext: IDoppeViewerActionContext<PROPS>, observableActionContext: ObservableValue<IDoppeViewerActionContext<PROPS>>) => DoppeActionComponentActionButtonProps;

    activateCTA?: (actionContext: IDoppeViewerActionContext<PROPS>, observableActionContext: ObservableValue<IDoppeViewerActionContext<PROPS>>) => void;

    loadViewerComponents?: () => Promise<VIEWER_COMP>;

    widgetPopupMode?: DoppeActionWidgetPopupMode;
}

export type DoppeActionTypeActionSettings = {};

export type DoppeActionTypeMetadata<PROPS extends DoppeActionTypeActionSettings, PARTIAL_PROPS extends DoppeActionTypeActionSettings = any> = {
    id: DoppeActionType.Id;

    /**
     * Even though the settings of an action of a specific type are stored on it, it's possible
     * that some properties were added to an action type since the action was previously saved.
     * This function will be called for any action that was instantiated from this action type to ensure
     * that the settings are as expected. Therefore, this function helps to ensure backward compatibility
     * along the way.
     */
    resolveActionSettings: (action: DoppeDtoAction<PARTIAL_PROPS>) => PROPS;

    actionSettingsMetadata: DoppeActionType.SettingsMetadata<PROPS>;
    actionListItemSettingsMetadata: DoppeActionType.ListItemsSettingsMetadata<PROPS>,

    canRenderInPage?: DoppeActionTypeRenderingInPageSupport;

    notSupportsSearch?: boolean;

    isWidgetOpener: boolean;

    isLinkOpener?: Evaluable<(actionContext: IDoppeViewerActionContext<PROPS>) => boolean>;

    isListItemLinkOpener?: boolean;

    notSupportsRenderingPreviewItem?: boolean;

    notSupportsListItemClickBeahviour?: boolean;

    notSupportsNoneItemClickBehaviour?: boolean;

    forceFullScreenOnListItemClick?: boolean;

    supportsListLayout?: boolean;

    /** Concrete layouts that this action type does NOT support. Used to compute supported {@link DoppeListActionLayoutCategory}. */
    notSupportedListLayouts?: DoppeActionLayout[];

    isModalOpener?: Evaluable<(actionContext: IDoppeViewerActionContext<PROPS>) => boolean>;

    hasNoGoalMeasuring?: boolean;

    hasNoLockInnerCTA?: boolean;

    skipReportingOnGoalAchievedOnActionButtonClick?: Evaluable<(actionContext: IDoppeViewerActionContext<PROPS>) => boolean>;

    withoutClientMetadata?: boolean;

    isPaymentAction?: boolean;
    isFormAction?: boolean;
    isLockAction?: boolean;
    getSupportedAutomationTrigger?: () => DoppeAutomationTriggers;
    isPremiumAction?: (premiumFeaturesStatus: DoppePremiumFeaturesStatus | null) => boolean;

    /**
     * Action that renders media should provide this method to resolve such media from its settings.
     * This can be used for:
     * - Background importing of external images that are used by the action.
     * - Used media suggestion when replacing a media in one of the page settings.
     */
    resolveUsedMedia?: (actionSettings: PROPS) => BymoPageMedia[];

    replaceExternalImages?: (actionSettings: PROPS, externalImagesUrlToImageResourceMap: Map<string, WixImageResource>) => PROPS;

    resolveUsedBusinessEntities?: (actionSettings: PROPS) => DoppeBusinessEntityReference[];

    resolveListItemsCount?: (actionSettings: PROPS) => number;

    lockIsNotSupported?: boolean;

    hasDisplaySettings?: boolean;

    resolveListItems?: (action: DoppeDtoAction<PROPS>, viewerLang: DoppeViewerLang, bymoPageProps: BymoPageProps, viewerPagesRoutesManager: DoppeViewerPagesRoutesManager, bymoPageContextProps: BymoPageContextProps) => DoppeDtoAction.ActionListItem<PROPS>[];

    isConnectedToExternalDataSource?: (actionSettings: PROPS) => boolean;

    resolveCouponData?: (actionSettings: PROPS, bymoPageContextProps: BymoPageContextProps) => DoppeDtoCouponCodeData;

    resolveCouponDataFromListItem?: (listItem: DoppeDtoAction.ActionListItem<PROPS>) => DoppeDtoCouponCodeData;

    resolveMainLink?: (actionSettings: PROPS, viewerLang: DoppeViewerLang, bymoPageProps: BymoPageProps, viewerPagesRoutesManager: DoppeViewerPagesRoutesManager, bymoPageContextProps: BymoPageContextProps) => string | null;

    resolveListSearchSettings?: (actionSettings: PROPS) => ListSearchSettings;

    resolveMainMedia?: (actionSettings: PROPS, bymoPageContextProps: BymoPageContextProps) => BymoPageMedia | null;

    prepareForViewer?: (actionSettings: PROPS, options: DoppeActionTypePrepareForViewerOptions) => void;

    resolveCustomWidgetPopupMode?: () => DoppeActionWidgetPopupMode;

    /**
     * Resolves the actual action settings by merging bound entity data when the action
     * is connected to an external entity (product, event, service, etc.).
     * For unbound actions, returns the settings unchanged.
     */
    resolveActualActionSettings?: (actionSettings: PROPS, bymoPageContextProps: BymoPageContextProps, bymoPageProps: BymoPageProps) => PROPS;

    /**
     * Resolves the actual settings for a single list item by merging bound entity data.
     * Returns undefined if the item is not found or the bound entity is unavailable.
     * For unbound items, returns the item settings unchanged.
     */
    resolveActualListItemSettings?: (listItemId: string, actionSettings: PROPS, bymoPageContextProps: BymoPageContextProps, bymoPageProps: BymoPageProps) => DoppeActionType.ResolveListItemsSettings<PROPS> | undefined;

    /** Determines whether the list items are static (user-curated) or dynamic (externally-sourced). */
    resolveListDataItemsSource?: (actionSettings: PROPS) => DoppeListDataItemsSourceInfo;

    /** Returns the external data sources available for this action type given the user's TPA installation state. */
    getSupportedDataSources?: (options: { hasLinkedMetaSiteTpaInstalled: boolean, hasCreatorsMetaSiteTpaInstalled: boolean }) => DoppeExternalDataSource[];

    /** Returns the business entity types this action can be bound to, based on installed TPAs. */
    getSupportedBoundEntities?: (options: DoppeActionTypeBoundEntitiesOptions) => DoppeBusinessEntity[];

    /** Returns the business entity types that list items of this action can be bound to, based on installed TPAs. */
    getSupportedListItemBoundEntities?: (options: DoppeActionTypeBoundEntitiesOptions) => DoppeBusinessEntity[];

    /**
     * Returns a static map of metasite type → TPA ID → supported bound entity types.
     * Unlike getSupportedBoundEntities, this requires no runtime TPA data, making it
     * suitable for static schema generation.
     */
    getSupportedBoundEntitiesByTpa?: () => DoppeActionTypeBoundEntitiesByTpaMap;

    /**
     * Returns a static map of metasite type → TPA ID → supported bound entity types for list items.
     * Unlike getSupportedListItemBoundEntities, this requires no runtime TPA data, making it
     * suitable for static schema generation.
     */
    getSupportedListItemBoundEntitiesByTpa?: () => DoppeActionTypeBoundEntitiesByTpaMap;

    /** Resolves which business entity the action is bound to, if any. */
    resolveActionEntityBinding?: (actionSettings: PROPS) => DoppeBusinessEntityReference | null;

    /**
     * Resolves which business entity a specific list item is bound to, if any.
     * In all current action type implementations, the entity binding info (provider + providerId)
     * is fully self-contained on the list item settings. The actionSettings parameter is included
     * for forward compatibility — future action types may configure the provider or binding context
     * at the action level rather than per-item.
     *
     * Note: The listItemSettings parameter is typed as `any` here to avoid TypeScript assignability
     * issues with the conditional type ResolveListItemsSettings in contravariant position.
     * Action type authors get strong typing via DoppeActionTypeMetadataInput (used by doppeActionTypeDefineMetadata).
     */
    resolveListItemEntityBinding?: (listItemSettings: any, actionSettings: PROPS) => DoppeBusinessEntityReference | null;

    /**
     * Inverse of {@link resolveActionEntityBinding}.
     * Given a business entity reference (e.g. from an AI spec's `boundEntity` field), returns the
     * partial settings that establish the binding (e.g. `{ productProvider, productProviderId }`).
     */
    resolveSettingsFromBoundEntityReference?: (ref: DoppeBusinessEntityReference) => Partial<PROPS>;

    /**
     * Same as {@link resolveSettingsFromBoundEntityReference} but for list item settings.
     * Given a business entity reference, returns the partial list item settings that establish
     * the binding for that item.
     */
    resolveListItemSettingsFromBoundEntityReference?: (ref: DoppeBusinessEntityReference) => Partial<DoppeActionType.ResolveListItemsSettings<PROPS>>;
}

export type DoppeActionTypePrepareForViewerOptions = {
    isLocked: boolean;
    isInnerCtaLocked: boolean;
    omitLockedActionsSettings: boolean;
}

export type DoppeActionTypeBoundEntitiesOptions = {
    linkedMetaSiteInstalledTpas: DoppeLinkedWixSiteSupportedTpas[];
    creatorsMetaSiteInstalledTpas: DoppeSupportedWixTpas[];
}

/** Static map of metasite type → installed TPA ID → supported bound entity types. */
export type DoppeActionTypeBoundEntitiesByTpaMap = Partial<
    Record<DoppeDtoUserMetaSiteType, Partial<Record<DoppeSupportedWixTpas, DoppeBusinessEntity[]>>>
>;

export type DoppeActionType<PROPS extends {}> = DoppeActionTypeMetadata<PROPS> & DoppeActionTypeViewerMetadata<PROPS>;

export namespace DoppeActionType {
    export type Id = string;


    export type InferProps<ACTION_TYPE extends DoppeActionTypeMetadata<any>> =
        ACTION_TYPE extends DoppeActionTypeMetadata<infer PROPS> ? PROPS : never;

    export type ResolveListItemsSettings<SETTINGS extends {}> =
        SETTINGS extends DoppeListActionSettings<infer ITEM_SETTINGS>
            ? ITEM_SETTINGS
            : never;

    export type ListItemsSettingsMetadata<SETTINGS extends {}> =
        SETTINGS extends DoppeListActionSettings<infer ITEM_SETTINGS>
            ? DoppeActionType.SettingsMetadata<OmitStrict<ITEM_SETTINGS, 'id' | 'enabled' | 'deleted' | 'isDummy' | 'displayInSearch'>>
            : IsAny<SETTINGS> extends true ? any : false;

    export type SettingsMetadata<SETTINGS extends {}> = {
        contentSettingsMetadata: BymoPagePropsContentSettingsMetadata<SETTINGS> | DoppeActionTypeContentSpecialSettingsMetadata;
        supportsContentSections: SETTINGS extends (DoppeActionContentSectionsSettings | DoppeListActionSettings.ListItemWithSections) ? {
            customContentSectionsTypes: Partial<Record<DoppeActionTypeSpecificContentSectionTypes, true>>;
        } : false;
        supportsActionCtaButtons: SETTINGS extends (DoppeActionCtaButtonsSettings | DoppeListActionSettings.ListItemWithCtaButtons) ? {
            customCtaButtonTypes: Partial<Record<DoppeActionTypeSpecificCTATypes, true>>;
        } : false;
        hideableContentSettingsNames: Record<Extract<keyof BymoPagePropsContentSettingsMetadata<SETTINGS>, ({
                [K in keyof SETTINGS]-?:
                [Extract<SETTINGS[K], HideableValueHiddenState<any>>] extends [never]
                    ? never
                    : IsAny<Extract<SETTINGS[K], HideableValueHiddenState<any>>['__hidden']> extends true
                        ? never
                        : K
            }[keyof SETTINGS])>, true>
            & Partial<Record<Exclude<keyof BymoPagePropsContentSettingsMetadata<SETTINGS>, ({
            [K in keyof SETTINGS]-?:
            [Extract<SETTINGS[K], HideableValueHiddenState<any>>] extends [never]
                ? never
                : IsAny<Extract<SETTINGS[K], HideableValueHiddenState<any>>['__hidden']> extends true
                    ? never
                    : K
        }[keyof SETTINGS])>, never>>;
        bindableContentSettingsNames: Record<Extract<keyof BymoPagePropsContentSettingsMetadata<SETTINGS>, ({
                [K in keyof SETTINGS]-?:
                [Extract<SETTINGS[K], OverridableValueOriginalState<any>>] extends [never]
                    ? never
                    : IsAny<Extract<SETTINGS[K], OverridableValueOriginalState<any>>['__original']> extends true
                        ? never
                        : K
            }[keyof SETTINGS])>, true>
            & Partial<Record<Exclude<keyof BymoPagePropsContentSettingsMetadata<SETTINGS>, ({
            [K in keyof SETTINGS]-?:
            [Extract<SETTINGS[K], OverridableValueOriginalState<any>>] extends [never]
                ? never
                : IsAny<Extract<SETTINGS[K], OverridableValueOriginalState<any>>['__original']> extends true
                    ? never
                    : K
        }[keyof SETTINGS])>, never>>;
    }

    export type InstantiationData<SETTINGS extends {}> = {
        settings?: Partial<SETTINGS extends DoppeListActionSettings<infer ITEM_SETTINGS> ? OmitStrict<SETTINGS, 'listDataItems'> & {listDataItems: Partial<ITEM_SETTINGS>[]} : SETTINGS>;
        lockSettings?: Partial<DoppeDtoAction['lockSettings']>;
        searchSettings?: Partial<DoppeDtoAction['searchSettings']>;
    } & OmitStrict<
        PartiallyRequired<Partial<DoppeDtoAction<SETTINGS>>, 'name'>,
        'settings' | 'isDummyAction' | 'actionTypeId' | 'lockSettings' | 'searchSettings'
    >

    export interface WidgetProps<PROPS extends {}> extends React.HTMLAttributes<any> {
        actionContext: IDoppeViewerActionContext<PROPS>;
    }
}

export function doppeActionTypeSupportsRenderingAsStrip (actionType: DoppeActionTypeMetadata<any>) {
    const renderingInPageSupport = actionType.canRenderInPage ?? DoppeActionTypeRenderingInPageSupport.Disabled;

    switch (renderingInPageSupport) {
        case DoppeActionTypeRenderingInPageSupport.Disabled:
        case DoppeActionTypeRenderingInPageSupport.AsCardOnly: {
            return false;
        }
        case DoppeActionTypeRenderingInPageSupport.FullyEnabled: {
            return true;
        }
    }
}

export function doppeActionTypeSupportsInPageView(actionType: DoppeActionTypeMetadata<any>) {

    const renderingInPageSupport = actionType.canRenderInPage ?? DoppeActionTypeRenderingInPageSupport.Disabled;

    switch (renderingInPageSupport) {
        case DoppeActionTypeRenderingInPageSupport.AsCardOnly:
        case DoppeActionTypeRenderingInPageSupport.FullyEnabled: {
            return true;
        }
        case DoppeActionTypeRenderingInPageSupport.Disabled: {
            return false;
        }
    }
}



export function doppeActionTypeSupportsListItemClickBehaviour (actionType: DoppeActionTypeMetadata<any>) {
    return !actionType.notSupportsListItemClickBeahviour && !actionType.forceFullScreenOnListItemClick;
}

export function doppeActionTypeSupportsListRendersPreviewItem (actionType: DoppeActionTypeMetadata<any>) {
    return !actionType.notSupportsRenderingPreviewItem;
}

export function doppeActionTypeSupportsNoneItemClickBehaviour (actionType: DoppeActionTypeMetadata<any>) {
    return !actionType.notSupportsNoneItemClickBehaviour;
}

export function doppeActionTypeIsListActionType (actionType: DoppeActionTypeMetadata<any>) {
    return actionType.resolveListItems !== undefined;
}

export function doppeActionTypeIsListActionSupportsLayout (actionType: DoppeActionTypeMetadata<any>) {
    return !!actionType.supportsListLayout;
}

export function doppeActionTypeGetNotSupportedListLayouts (actionType: DoppeActionTypeMetadata<any>): DoppeActionLayout[] | undefined {
    return actionType.notSupportedListLayouts;
}

export function doppeActionTypeIsSupportsLock (actionType: DoppeActionTypeMetadata<any>) {
    return actionType.lockIsNotSupported !== true;
}

export function doppeActionTypeHasDisplaySettings (actionType: DoppeActionTypeMetadata<any>) {
    return actionType.hasDisplaySettings !== false;
}


export function doppeActionTypeGetSupportedAutomationTrigger (actionType: DoppeActionTypeMetadata<any>) {
    return actionType.getSupportedAutomationTrigger?.() ?? null satisfies DoppeAutomationTriggers | null;
}


export function doppeActionTypeSupportsLockInnerCTA(actionType: DoppeActionTypeMetadata<any>) {
    return !actionType.hasNoLockInnerCTA;
}

export function doppeActionTypeSupportsWidgetOpening(actionType: DoppeActionTypeMetadata<any>) {
    return actionType.isWidgetOpener;
}

export function doppeActionTypeSupportsRenderingAsButton (actionType: DoppeActionTypeMetadata<any>) {
    if (doppeActionTypeSupportsInPageView(actionType)) {
        return doppeActionTypeSupportsWidgetOpening(actionType);
    } else {
        return true;
    }
}

export function doppeActionTypeSupportsOpeningWidgetAsPopup (actionType: DoppeActionTypeMetadata<any>) {
    return doppeActionTypeSupportsWidgetOpening(actionType) &&
        (doppeActionTypeSupportsInPageView(actionType) || actionType.isModalOpener);
}

export function doppeActionTypeSupportsOpeningWidgetAsLink (actionType: DoppeActionType<any>) {
    return actionType.isLinkOpener ?? false;
}

export function doppeActionTypeSupportsOpeningListItemWidgetAsLink (actionType: DoppeActionType<any>) {
    return actionType.isListItemLinkOpener ?? false;
}

export function doppeActionTypeCreateSettingsResolvingFunction<SETTINGS extends {}>(settings: SETTINGS) {
    return (action: DoppeDtoAction<any>) => {

        const actionSettings = action.settings;

        return objectMapValues(settings, (value, key) => {
            return actionSettings[key] ?? value;
        }) as SETTINGS;
    }
}

export function doppeActionTypeResolveSettingsFromAction<SETTINGS extends {}>(
    actionType: DoppeActionType<SETTINGS>,
    action: DoppeDtoAction<SETTINGS>
) {
    return actionType.resolveActionSettings(action)
}

/**
 * Resolves the actual action settings by delegating to the action type's resolveActualActionSettings
 * if available. For action types without bound entity support, returns the settings unchanged.
 */
export function doppeActionTypeResolveActualActionSettings<SETTINGS extends {}>(
    actionType: DoppeActionTypeMetadata<SETTINGS>,
    actionSettings: SETTINGS,
    bymoPageContextProps: BymoPageContextProps,
    bymoPageProps: BymoPageProps,
): SETTINGS {
    return actionType.resolveActualActionSettings?.(actionSettings, bymoPageContextProps, bymoPageProps) ?? actionSettings;
}

export function doppeActionTypeResolveActualListItemSettings<SETTINGS extends {}>(
    actionType: DoppeActionTypeMetadata<SETTINGS>,
    listItemId: string,
    actionSettings: SETTINGS,
    bymoPageContextProps: BymoPageContextProps,
    bymoPageProps: BymoPageProps,
): DoppeActionType.ResolveListItemsSettings<SETTINGS> | undefined {
    return actionType.resolveActualListItemSettings?.(listItemId, actionSettings, bymoPageContextProps, bymoPageProps);
}

export function doppeActionTypeResolveListDataItemsSource<
    PROPS extends DoppeActionTypeActionSettings,
    PARTIAL_PROPS extends DoppeActionTypeActionSettings,
>(
    actionType: DoppeActionTypeMetadata<PROPS, PARTIAL_PROPS>,
    actionSettings: PARTIAL_PROPS,
): DoppeListDataItemsSourceInfo {
    const resolvedSettings = actionType.resolveActionSettings({settings: actionSettings} as DoppeDtoAction<PARTIAL_PROPS>);
    return actionType.resolveListDataItemsSource?.(resolvedSettings) ?? {type: DoppeListDataItemsSource.Static};
}

export function doppeActionTypeResolveActionEntityBinding<
    PROPS extends DoppeActionTypeActionSettings,
    PARTIAL_PROPS extends DoppeActionTypeActionSettings,
>(
    actionType: DoppeActionTypeMetadata<PROPS, PARTIAL_PROPS>,
    actionSettings: PARTIAL_PROPS,
): DoppeBusinessEntityReference | null {
    const resolvedSettings = actionType.resolveActionSettings({settings: actionSettings} as DoppeDtoAction<PARTIAL_PROPS>);
    return actionType.resolveActionEntityBinding?.(resolvedSettings) ?? null;
}

export function doppeActionTypeResolveListItemEntityBinding<
    PROPS extends DoppeActionTypeActionSettings,
    PARTIAL_PROPS extends DoppeActionTypeActionSettings,
>(
    actionType: DoppeActionTypeMetadata<PROPS, PARTIAL_PROPS>,
    listItemSettings: DoppeListActionSettings.ListItemBaseSettings,
    actionSettings: PARTIAL_PROPS,
): DoppeBusinessEntityReference | null {
    const resolvedSettings = actionType.resolveActionSettings({settings: actionSettings} as DoppeDtoAction<PARTIAL_PROPS>);
    return actionType.resolveListItemEntityBinding?.(listItemSettings, resolvedSettings) ?? null;
}

export function doppeActionTypeResolveSettingsFromBoundEntityReference(
    actionType: DoppeActionTypeMetadata<any>,
    ref: DoppeBusinessEntityReference
): Record<string, unknown> | null {
    return (actionType.resolveSettingsFromBoundEntityReference?.(ref) as Record<string, unknown>) ?? null;
}

export function doppeActionTypeResolveListItemSettingsFromBoundEntityReference(
    actionType: DoppeActionTypeMetadata<any>,
    ref: DoppeBusinessEntityReference
): Record<string, unknown> | null {
    return (actionType.resolveListItemSettingsFromBoundEntityReference?.(ref) as Record<string, unknown>) ?? null;
}

export function doppeActionTypeGetSupportedBoundEntities(
    actionType: DoppeActionTypeMetadata<any>,
    options: DoppeActionTypeBoundEntitiesOptions
): DoppeBusinessEntity[] {
    return actionType.getSupportedBoundEntities?.(options) ?? [];
}

export function doppeActionTypeGetSupportedListItemBoundEntities(
    actionType: DoppeActionTypeMetadata<any>,
    options: DoppeActionTypeBoundEntitiesOptions
): DoppeBusinessEntity[] {
    return actionType.getSupportedListItemBoundEntities?.(options) ?? [];
}

export function doppeActionTypeGetSupportedBoundEntitiesByTpa(
    actionType: DoppeActionTypeMetadata<any>
): DoppeActionTypeBoundEntitiesByTpaMap {
    return actionType.getSupportedBoundEntitiesByTpa?.() ?? {};
}

export function doppeActionTypeGetSupportedListItemBoundEntitiesByTpa(
    actionType: DoppeActionTypeMetadata<any>
): DoppeActionTypeBoundEntitiesByTpaMap {
    return actionType.getSupportedListItemBoundEntitiesByTpa?.() ?? {};
}

/**
 * Resolves the list of supported bound entity types from a TPA map given the currently installed TPAs.
 * Used both at runtime and in tests to verify consistency with getSupportedBoundEntities.
 */
export function doppeActionTypeBoundEntitiesByTpaMapResolveEntities(
    map: DoppeActionTypeBoundEntitiesByTpaMap,
    options: DoppeActionTypeBoundEntitiesOptions
): DoppeBusinessEntity[] {
    return arrayRemoveNullishValues([
        ...iterableMapToArray(
            objectGetEntries(map[DoppeDtoUserMetaSiteType.LinkedMetaSite] ?? {}),
            ([tpaId, entities], skip) =>
                options.linkedMetaSiteInstalledTpas.includes(tpaId as DoppeLinkedWixSiteSupportedTpas)
                    ? entities
                    : skip
        ).flat(),
        ...iterableMapToArray(
            objectGetEntries(map[DoppeDtoUserMetaSiteType.CreatorsMetaSite] ?? {}),
            ([tpaId, entities], skip) =>
                options.creatorsMetaSiteInstalledTpas.includes(tpaId as DoppeSupportedWixTpas)
                    ? entities
                    : skip
        ).flat(),
    ]);
}


/**
 * @deprecated You should not create a DoppeActionType object, but to register the metadata and the viewer metadata in
 * DoppeActionTypesStore and in DoppeActionTypesMetadataStore
 */
export function doppeActionTypeDefineNew<PROPS extends {} = {}>(
    actionTypeMetadata: DoppeActionTypeMetadata<PROPS>,
    actionTypeViewerMetadata: DoppeActionTypeViewerMetadata<PROPS>
) : DoppeActionType<PROPS> {
    return {
        ...actionTypeMetadata,
        ...actionTypeViewerMetadata
    }
}

/**
 * Input type for doppeActionTypeDefineMetadata that provides strong typing for
 * resolveListItemEntityBinding. The listItemSettings parameter is typed as
 * ResolveListItemsSettings<PROPS> (the concrete item type) instead of `any`,
 * giving action type authors full type checking at definition time.
 */
export type DoppeActionTypeMetadataInput<PROPS extends DoppeActionTypeActionSettings, PARTIAL_PROPS extends DoppeActionTypeActionSettings = any> =
    OmitStrict<DoppeActionTypeMetadata<PROPS, PARTIAL_PROPS>, 'resolveListItemEntityBinding'> &
    (PROPS extends DoppeListActionSettings<infer ITEM_SETTINGS>
        ? { resolveListItemEntityBinding?: (listItemSettings: ITEM_SETTINGS, actionSettings: PROPS) => DoppeBusinessEntityReference | null }
        : {});

export function doppeActionTypeDefineMetadata<PROPS extends {} = {}, PARTIAL_AND_DEPRECATED_PROPS extends {} = Partial<PROPS>>(
    actionType: DoppeActionTypeMetadataInput<PROPS, PARTIAL_AND_DEPRECATED_PROPS>
) {
    return actionType as DoppeActionTypeMetadata<PROPS, PARTIAL_AND_DEPRECATED_PROPS>;
}

export function doppeActionTypeDefineViewerMetadata<PROPS extends {} = {}, VIEWER_COMP = unknown>(
    _actionType: DoppeActionTypeMetadata<PROPS>,
    viewerMetadata: DoppeActionTypeViewerMetadata<PROPS, VIEWER_COMP>
) {
    return viewerMetadata;
}

export function doppeActionTypeInstantiate<SETTINGS extends {}>(
    actionType: DoppeActionType<SETTINGS>,
    actionData: DoppeActionType.InstantiationData<SETTINGS>
): DoppeDtoAction<SETTINGS> {

    const action = doppeDtoActionExtendWithDefaultProps({
        id: actionData.id ?? uuidv4(),
        actionTypeId: actionType.id,
        name: actionData.name,
        callToActionDescription: actionData.callToActionDescription ?? hideableValueCreateHidden(''),
        enabled: actionData.enabled ?? true,
        icon: actionData.icon,
        widgetOpeningMode: actionData.widgetOpeningMode,
        settings: {
            ...actionData.settings
        },
        viewType: actionData.viewType,
        showActionHeader: actionData.showActionHeader ?? true,
        tag: actionData.tag,
        searchSettings: {
            ...doppeDtoActionGetDefaultSearchSettingsProps(),
            ...actionData.searchSettings
        },
        bannerMedia: actionData.bannerMedia ?? null,
        lockSettings:  {
            ...doppeDtoActionGetDefaultLockSettingsProps(),
            ...actionData.lockSettings
        },
        __aiGenerationMetadata: actionData.__aiGenerationMetadata
    }) satisfies DoppeDtoAction;

    return {
        ...action,
        settings: doppeActionTypeResolveSettingsFromAction(actionType, action)
    }
}

export function doppeActionTypeInstantiateLockAction<SETTINGS extends {}>(
    actionType: DoppeActionType<SETTINGS>,
    actionData: DoppeActionType.InstantiationData<SETTINGS>
): DoppeDtoAction<SETTINGS> {

    return doppeActionTypeInstantiate(actionType, {
        ...actionData,
    });
}

export interface IDoppeActionFactory {

    createAction<ACTION_TYPE extends DoppeActionType<any>>(
        actionType: ACTION_TYPE,
        actionData: {
            name: DoppeDtoAction['name'];
            enabled?: DoppeDtoAction['enabled'];
            viewType?: DoppeDtoAction['viewType'];
            showActionHeader?: DoppeDtoAction['showActionHeader'];
            settings: DoppeActionType.InferProps<ACTION_TYPE>
        }
    ): DoppeDtoAction<DoppeActionType.InferProps<ACTION_TYPE>>;
}

export const DoppeActionsSourceType = {
    Page: 'page',
    Search: 'search',
} as const;

export type DoppeActionsSourceType = Values<typeof DoppeActionsSourceType>;


export const DoppeActionGoalType = {
    Share: 'share',
    Click: 'click',
    WhatsappClick: 'whatsappClick',
    CallButtonClick: 'callButtonClick',
    EmailButtonClick: 'emailButtonClick',
    NavigateButtonClick: 'navigateButtonClick',
    FacebookMessageButtonClick: 'facebookMessageButtonClick',
    InstagramMessageButtonClick: 'instagramMessageButtonClick',
    SocialIconClick: 'socialIconClick',
    SearchClick: 'searchClick',
    CouponCopied: 'couponCopied',
    FormSubmission: 'formSubmission',
    Subscribe: 'subscribe',
    Payment: 'payment',
    Donation: 'donate',
    Views: 'view',
    EmbedWidgetViewed: 'embedWidgetViewed',
    SaveContact: 'saveContact',
    PlayVideo: 'playVideo',
} as const;

export type DoppeActionGoalType = Values<typeof DoppeActionGoalType>;

export const WidgetRenderingContextType = {
    Card: 'Card',
    Strip: 'Strip',
    Modal: 'Modal',
} as const;

export type WidgetRenderingContextType = Values<typeof WidgetRenderingContextType>;

export const WidgetModalRenderingContextType = enumReduce(WidgetRenderingContextType, [
    WidgetRenderingContextType.Modal
]);

export type WidgetModalRenderingContextType = Values<typeof WidgetModalRenderingContextType>;

/**
 * Kind of entity protected by a lock action. Either a single content action
 * inside a page (`Action`) or an entire inner page (`Page`). Used by the
 * Payment Lock to tell the server which lookup path to take when validating
 * an order against the lock the buyer is trying to open.
 */
export const DoppeLockedEntityType = {
    Action: 'action',
    Page: 'page',
} as const;

export type DoppeLockedEntityType = Values<typeof DoppeLockedEntityType>;

export interface WidgetLockController {
    unlock: (unlockedMetaData?: any) => Promise<boolean>;
    /**
     * Durable id of the locked entity. For `Action` locks this is a content
     * action id; for `Page` locks it is the inner page id. Field name is
     * preserved for backwards compatibility — read together with
     * `lockedEntityType` to know which kind of id it carries.
     */
    lockedActionId: string;
    lockedEntityType: DoppeLockedEntityType;
}


